import { useGet, usePost } from "@/apiHooks/HttpFunctions";
import { setSelecteColumnsApiEndPoint } from "@/apis/apis";
import RenderToast from "@/components/RenderToast";
import { dateFormatOptions } from "@/constant/Tables/date-format";
import getEditTableInitialsValue from "@/data/UserManagement/initial-values";
import { useFormik } from "formik";
import { useCallback, useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import Button from "../../components/Button";
import DraggableSample from "../../components/DraggableSample";
import DropDown from "../../components/DropDown/DropDown";
import ModalSkeleton from "../../components/ModalSkeleton";
import { EditTableSchema } from "../../schema/EditTableSchema";
import { mergeClass } from "../../utils/helper";
import classes from "./EditTableModal.module.css";

export default function EditTableModal({
  title,
  setShow,
  show,
  allColumns,
  setColumnConfigs,
  loading,
}) {
  const [shownColumns, setShownColumns] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  // Get the last segment from URL for API calls
  const pathSegments = window.location.pathname.split("/");
  const lastSegment = pathSegments[pathSegments.length - 1];

  // Fetch selected columns data
  const { data: selectedColumnsData } = useGet({
    apiURL: `${setSelecteColumnsApiEndPoint}/${lastSegment}`,
  });

  // Save table configs
  const { mutate: saveEditTable } = usePost({
    apiURL: setSelecteColumnsApiEndPoint,
    onSuccess: () => {
      closeModal();
      RenderToast({
        type: "success",
        message: "Table updated successfully",
      });
    },
  });

  // Formik setup
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getEditTableInitialsValue(selectedColumnsData),
    validationSchema: EditTableSchema,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  // Reset shown and hidden columns
  const resetColumns = useCallback(() => {
    const selectedColumns = selectedColumnsData?.data?.columns || [];

    setShownColumns(
      selectedColumns.map((column) => {
        const matchedColumn = allColumns.find(
          (col) => col.accessorKey === column || col.id === column
        );

        return {
          id: matchedColumn?.accessorKey ?? matchedColumn?.id,
          label: matchedColumn?.header ?? matchedColumn?.accessorKey,
          value: matchedColumn?.accessorKey ?? matchedColumn?.id,
        };
      })
    );

    setHiddenColumns(
      allColumns
        .filter(
          (column) =>
            !selectedColumns.includes(column.id ?? column.accessorKey) &&
            (column.id ?? column.accessorKey) !== "actions"
        )
        .map((column) => ({
          id: column.accessorKey ?? column.id,
          label: column.header ?? column.accessorKey,
          value: column.accessorKey ?? column.id,
        }))
    );
  }, [allColumns, selectedColumnsData?.data?.columns]);

  // Handle modal close
  const closeModal = useCallback(() => {
    formik.resetForm();
    setShow(false);
    resetColumns();
  }, [formik, setShow, resetColumns]);

  // Update column configs on selectedColumnsData change
  useEffect(() => {
    setColumnConfigs(selectedColumnsData?.data);
    resetColumns();
  }, [resetColumns, selectedColumnsData, setColumnConfigs]);

  // Handle table settings submission
  const handleTableHeadings = async (params) => {
    const payload = {
      ...params,
      page: lastSegment,
    };
    saveEditTable(payload);
  };

  const handleSubmit = async () => {
    const data = {
      columns: [...new Set(shownColumns.map((v) => v.value))],
      date_format: formik.values.date_format,
    };

    await handleTableHeadings(data);
  };

  return (
    <ModalSkeleton
      header={title}
      setShow={setShow}
      show={show}
      showCloseIcon={true}
      showLogo={true}
    >
      <Container fluid>
        <Row>
          <Col md={12} className="p-0 text-center">
            <span>
              Drag and drop left and right for displayed columns and up and down
              for sequence.
            </span>
          </Col>
        </Row>
        <Row>
          <Col md={12} className="p-0">
            <DraggableSample
              list1={shownColumns}
              list2={hiddenColumns}
              setList1={setShownColumns}
              setList2={setHiddenColumns}
              setSelectedItem={setSelectedItem}
              selectedItem={selectedItem}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <div>
              <DropDown
                showRainbowBorder={true}
                setValue={(selected) =>
                  formik.setFieldValue("date_format", selected.value)
                }
                errorText={
                  formik.touched.date_format && formik.errors.date_format
                }
                classNamePrefix={classes.dropDownClassPrefix}
                dropDownClass={classes.dropDownClass}
                value={dateFormatOptions.find(
                  (option) => option.value === formik.values.date_format
                )}
                options={dateFormatOptions}
                menuPlacement={"top"}
                label={"Edit Date Format"}
                placeholder={"Any"}
                mainClass={classes.dropDown}
              />
            </div>
          </Col>
        </Row>
        <div className="mt-4 gap-x-10 flex justify-center">
          <div className="w-1/3">
            <Button
              variant={"outlined"}
              disabled={loading}
              className={mergeClass(classes.buttonOne, classes.button)}
              label={"Cancel"}
              onClick={closeModal}
            />
          </div>
          <div className="w-1/3">
            <Button
              disabled={loading}
              className={mergeClass(classes.buttonTwo, classes.button)}
              variant={"green-gradient"}
              label={loading ? "Saving..." : "Save"}
              onClick={handleSubmit}
            />
          </div>
        </div>
      </Container>
    </ModalSkeleton>
  );
}
